/**
 * .ui-btn / .gform_button / .social-icon: bubble hover effect – circle expands from cursor
 * with hover background colour (CodePen-style: https://codepen.io/Lunoware/pen/jKJRVr), plus pulse.
 * Also targets Gravity Forms footer buttons and .social-icon (uses --social-icon-hover-bg).
 * No-ops if no matching elements exist.
 */
import gsap from "gsap";

const BTN_SELECTOR =
	".ui-btn, .gform_button, .gform_next_button, .gform_previous_button, .gform_button_select_files, .social-icon, .swiper-ui__btn";

const BUBBLE_EXPAND = {
	scale: 1,
	duration: 0.5,
	ease: "power2.out",
};

const BUBBLE_SHRINK = {
	scale: 0,
	duration: 0.4,
	ease: "power2.out",
};

/** Slower timings for .social-icon */
const BUBBLE_EXPAND_SLOW = { ...BUBBLE_EXPAND, duration: 0.7 };
const BUBBLE_SHRINK_SLOW = { ...BUBBLE_SHRINK, duration: 0.55 };
const PULSE_UP_SLOW = 0.22;
const PULSE_DOWN_SLOW = 0.28;

/** Minimum size of the circle element; larger buttons get a bigger circle so the bubble always fills */
const BUBBLE_SIZE_MIN = 500;

/** Scale factor so bubble fully covers the button */
const BUBBLE_COVER_SCALE = 2;

/** Bubble size so that when scaled, the circle covers the whole button from any entry point (uses button diagonal) */
function getBubbleSize(width, height) {
	const diagonal = Math.sqrt(width * width + height * height);
	return Math.max(BUBBLE_SIZE_MIN, Math.ceil(diagonal));
}

/** Single pulse on hover: scale up then back */
const PULSE_SCALE = 1.06;
const PULSE_UP_DURATION = 0.15;
const PULSE_DOWN_DURATION = 0.2;

function getHoverColor(el) {
	const computed = getComputedStyle(el);
	const varName = el.classList.contains("social-icon")
		? "--social-icon-hover-bg"
		: "--ui-btn-hover-bg";
	return computed.getPropertyValue(varName).trim() || computed.backgroundColor;
}

function positionCircleAt(circle, x, y) {
	circle.style.left = `${x}px`;
	circle.style.top = `${y}px`;
}

export function initUiBtnHover() {
	const btns = document.querySelectorAll(BTN_SELECTOR);
	if (!btns.length) return;

	btns.forEach((btn) => {
		let circle = null;
		let expandTween = null;
		let shrinkTween = null;
		let pulseTween = null;

		function runPulse() {
			if (pulseTween) pulseTween.kill();
			gsap.set(btn, { scale: 1 });
			const isSlow = btn.classList.contains("social-icon");
			const upDur = isSlow ? PULSE_UP_SLOW : PULSE_UP_DURATION;
			const downDur = isSlow ? PULSE_DOWN_SLOW : PULSE_DOWN_DURATION;
			pulseTween = gsap
				.timeline({ overwrite: true })
				.to(btn, {
					scale: PULSE_SCALE,
					duration: upDur,
					ease: "power2.out",
				})
				.to(btn, {
					scale: 1,
					duration: downDur,
					ease: "power2.in",
				});
		}

		function ensureCircle() {
			const rect = btn.getBoundingClientRect();
			const size = getBubbleSize(rect.width, rect.height);
			if (circle && circle.parentNode === btn) {
				circle.style.width = `${size}px`;
				circle.style.height = `${size}px`;
				circle.style.marginLeft = `${-size / 2}px`;
				circle.style.marginTop = `${-size / 2}px`;
				return circle;
			}
			circle = document.createElement("span");
			circle.setAttribute("aria-hidden", "true");
			circle.className = btn.classList.contains("social-icon")
				? "social-icon__hover-circle"
				: "ui-btn__hover-circle";
			circle.style.cssText = `
				position: absolute;
				width: ${size}px;
				height: ${size}px;
				margin-left: -${size / 2}px;
				margin-top: -${size / 2}px;
				left: 0;
				top: 0;
				border-radius: 50%;
				pointer-events: none;
				background-color: ${getHoverColor(btn)};
				transform-origin: center center;
			`;
			btn.insertBefore(circle, btn.firstChild);
			return circle;
		}

		btn.addEventListener("mouseenter", (e) => {
			if (btn.classList.contains("swiper-button-disabled") || btn.disabled) return;
			const rect = btn.getBoundingClientRect();
			const x = e.clientX - rect.left;
			const y = e.clientY - rect.top;

			runPulse();

			const el = ensureCircle();
			positionCircleAt(el, x, y);

			if (shrinkTween) shrinkTween.kill();
			gsap.set(el, { scale: 0 });
			const expandOpts = btn.classList.contains("social-icon")
				? BUBBLE_EXPAND_SLOW
				: BUBBLE_EXPAND;
			expandTween = gsap.to(el, {
				scale: BUBBLE_COVER_SCALE,
				...expandOpts,
				overwrite: true,
			});
		});

		btn.addEventListener("mouseleave", (e) => {
			if (btn.classList.contains("swiper-button-disabled") || btn.disabled) return;
			const rect = btn.getBoundingClientRect();
			const x = e.clientX - rect.left;
			const y = e.clientY - rect.top;

			if (pulseTween) pulseTween.kill();
			gsap.set(btn, { scale: 1 });

			if (!circle || circle.parentNode !== btn) return;
			positionCircleAt(circle, x, y);

			if (expandTween) expandTween.kill();
			const shrinkOpts = btn.classList.contains("social-icon")
				? BUBBLE_SHRINK_SLOW
				: BUBBLE_SHRINK;
			shrinkTween = gsap.to(circle, {
				scale: 0,
				...shrinkOpts,
				overwrite: true,
			});
		});
	});
}
